import { Box, Checkbox, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import StepperComp from "../common/Stepper";
import { stepsList } from "../../utils/constants";
import ReusableForm from "../common/ReusableForm";
import { addStaffValidationSchema } from "../forms/validationSchemas";
import { addStaffInitialValues } from "../forms/initialValues";
import DigitalPinForm from "../common/DigitalPinForm";
import UserPermission from "../common/UserPermission";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getAllPharmacyPermission,
  registerStaffPermission,
} from "../../features/staff/staffSlice";
import AppButton from "../common/AppButton";
import AppErrorMessage from "../common/AppErrorMessage";
import { Roles } from "../../utils/constants";

interface IProps {
  handleClose: () => void;
  setOpenSnackbar: any;
  GetAllStaffPermissions: () => void;
  setOpenErrorToast: any;
}

const AddStaffModal = ({
  handleClose,
  setOpenSnackbar,
  GetAllStaffPermissions,
  setOpenErrorToast,
}: IProps) => {
  const {
    isRegisterStaffPermissionLoading,
    registerStaffPermissionMessage,
    isRegisterStaffPermissionError,
  }: any = useAppSelector((state) => state.staff);

  const dispatch = useAppDispatch();
  const pharmacyId = localStorage?.getItem("pharmacyId");
  const {
    staffComponentPayload: { data: allComponents },
  }: any = useAppSelector((state) => state.staff);

  const {
    permissionsPayload: { data: staffPermissions },
  }: any = useAppSelector((state) => state.staff);

  const formRef = useRef<any>(null);

  const [registerStaffData, setRegisterStaffData] = useState<any>({
    pharmacy_id: pharmacyId,
  });

  const authType: any = 2;
  const filterIndices = authType === 1 ? [2] : authType === 0 ? [11, 14] : [];

  const GetStaffPermissions = useCallback(() => {
    dispatch(getAllPharmacyPermission());
  }, [dispatch]);

  useEffect(() => {
    GetStaffPermissions();
  }, [GetStaffPermissions]);

  // const [allComponentsData, setAllComponentsData] = useState<any>([]);
  // useEffect(() => {
  //   if (allComponents) {
  //     const updatedComponents = allComponents?.filter(
  //       (_: any, index: number) => !filterIndices?.includes(index)
  //     );
  //     setAllComponentsData(updatedComponents);
  //   }
  // }, [authType, allComponents]);

  const handleSubmit = (module_name: string, submodule: string) => {
    const permission = {
      module: module_name,
      submodule: submodule,
    };
    // Check if the permission already exists in registerStaffData
    setRegisterStaffData((prevData: any) => {
      const existingPermissions = prevData.permission || [];
      const permissionExists = existingPermissions.some(
        (item: any) =>
          item.module === module_name && item.submodule === submodule
      );

      if (!permissionExists) {
        return {
          ...prevData,
          permission: [...existingPermissions, permission],
        };
      }
      return prevData;
    });
  };

  const handleDelete = (module_name: string, submodule: string) => {
    setRegisterStaffData((prevData: any) => {
      const existingPermissions = prevData.permission || [];
      const updatedPermissions = existingPermissions.filter(
        (item: any) =>
          !(item.module === module_name && item.submodule === submodule)
      );

      return {
        ...prevData,
        permission: updatedPermissions,
      };
    });
  };

  const displayTablePermissions = (
    column: string,
    module_name: string,
    permissions: Array<any>
  ) => {
    const exists = permissions.some((item) => {
      return column === item.submodule;
    });

    if (exists) {
      const isChecked = staffPermissions?.some((item: any) => {
        if (item.module_name === module_name) {
          return item.permissions.some((item2: any) => {
            return item2.submodule === column;
          });
        } else {
          return false;
        }
      });

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Checkbox
            sx={{
              color: "#C4C4C4",
              "&.Mui-checked": {
                color: "#0055AB",
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
            checked={isChecked}
            onChange={(e: any) => {
              if (e.target.checked === true) {
                handleSubmit(module_name, column);
              } else {
                handleDelete(module_name, column);
              }
            }}
          />
        </Box>
      );
    } else {
      return null;
    }
  };

  const options = Object.keys(Roles)
    .filter((key: any) => isNaN(Number(key)))
    .map((key: any) => ({
      id: Roles[key as keyof typeof Roles],
      name: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
    }));

  const genderOptions = [
    { id: "1", name: "Male" },
    { id: "2", name: "Female" },]
  const data = [
    {
      id: 1,
      title: "Name",
      name: "name",
      type: "text",
      placeholder: "Enter Name",
    },
    {
      id: 2,
      type: "select",
      title: "Role",
      name: "role_id",
      placeholder: "Enter Role",
      options: options,
    },

    {
      id: 3,
      title: "Email",
      name: "email",
      type: "email",
      placeholder: "Enter Email",
    },
    {
      id: 4,
      title: "Number",
      name: "contact_number",
      type: "contact_number",
      placeholder: "03xx xxxxxxxxx",
    },
    {
      id: 5,
      title: "Address",
      name: "address",
      type: "address",
      placeholder: "Enter Address",
    },
    {
      id: 6,
      title: "Gender",
      name: "gender",
      type: "select",
      placeholder: "Enter Gender",
      options: genderOptions
    },
  ];

  const [step, setStep] = useState(0);
  const handleSubmitRegister = (values: any) => {
    setRegisterStaffData((prevData: any) => ({
      ...prevData,
      ...values,
    }));
    setStep((prevStep) => prevStep + 1);
  };

  const handleSubmitPin = (values: any) => {
    const pin = values;
    setRegisterStaffData((prevData: any) => ({
      ...prevData,
      pin,
    }));
    setStep((prevStep) => prevStep + 1);
  };

  const handleSubmitPermission = () => {
    dispatch(registerStaffPermission(registerStaffData)).then((res: any) => {
      if (res.payload.success === true) {
        setOpenSnackbar(true);
        GetAllStaffPermissions();
        handleClose();
      } else {
        setOpenErrorToast(true);
      }
    });
  };

  const getStepContent = () => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ width: "100%" }}>
            <ReusableForm
              handleSubmit={handleSubmitRegister}
              data={data}
              validationSchema={addStaffValidationSchema}
              initialValue={addStaffInitialValues}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ width: "100%" }}>
            <DigitalPinForm
              title=" Create 6 Digit Pin for your staff member"
              length={6}
              onSubmit={handleSubmitPin}
              ref={formRef}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ width: "100%" }}>
            <UserPermission
              displayTablePermissions={displayTablePermissions}
              allComponentsData={allComponents}
            />
          </Box>
        );
    }
  };

  return (
    <>
      <Box
        sx={{
          marginTop: { xs: 1, sm: 3.063 },
          marginBottom: { xs: 0.5, sm: 1.125 },
          width: "100%",
          display: "flex",
          justifyContent: { xs: "start", sm: "center" },
          alignItems: { xs: "start", sm: "center" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: "50%" },
          }}
        >
          <StepperComp activeStep={step} stepsList={stepsList} />
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        {getStepContent()}
        {step < 3 && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              {step > 1 && (
                <AppButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  title={"Back"}
                  onClick={() => {
                    setStep(step - 1);
                  }}
                  sx={{ float: "right" }}
                />
              )}

              <AppButton
                variant="contained"
                color="primary"
                type="submit"
                title={
                  isRegisterStaffPermissionLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : step < 2 ? (
                    "Next"
                  ) : (
                    "Done"
                  )
                }
                onClick={() => {
                  if (step === 0) {
                    document
                      .querySelector("form")
                      ?.dispatchEvent(
                        new Event("submit", { cancelable: true, bubbles: true })
                      );
                  } else if (step === 1) {
                    formRef.current?.submitForm();
                  } else if (step === 2) {
                    handleSubmitPermission();
                  }
                }}
                sx={{ float: "right" }}
                disabled={isRegisterStaffPermissionLoading}
              />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AddStaffModal;
